import { createSlice, PayloadAction } from "@reduxjs/toolkit";

import { OFFLINE_QUEUE_APP, OFFLINE_QUEUE_APPS, type OfflineQueueApp } from "~/helpers/offline/queueMeta";
import {
	QUEUE_ENTRY_STATUS,
	type QueueEntryDescriptor,
	type QueueEntryStatus,
	type QueueFailureReasonKind,
	type QueueRefusal
} from "~/helpers/sw/messageContract";

export interface OfflineQueueEntry {
	app: OfflineQueueApp;
	attempts: number;
	clientId: string;
	detail: string | null;
	enqueuedAt: number;
	labelKey: string | null;
	lastAttemptAt: number | null;
	mutationName: string;
	reason: string | null;
	reasonKind: QueueFailureReasonKind | null;
	reconciliationId: string | null;
	refusal?: QueueRefusal;
	retryable: boolean;
	status: QueueEntryStatus;
}

export interface OfflineQueueAppState {
	failed: OfflineQueueEntry[];
	queued: OfflineQueueEntry[];
}

export interface AdmittedQueueEntry {
	app: OfflineQueueApp;
	mutationName: string;
	reconciliationId: string;
}

export interface OfflineQueueState {
	byApp: Record<OfflineQueueApp, OfflineQueueAppState>;
	isQueueReadFailed: boolean;
	isQueueUnavailable: boolean;
	isReplaying: boolean;
	isSnapshotLoaded: boolean;
	recentlyAdmitted: AdmittedQueueEntry[];
}

export interface FailedQueueEntryPayload extends QueueEntryDescriptor {
	reason: string;
	reasonKind?: QueueFailureReasonKind;
	retryable?: boolean;
}

export const RECENTLY_ADMITTED_LIMIT = 50;

const emptyAppState = (): OfflineQueueAppState => ({ failed: [], queued: [] });

const buildInitialByApp = (): Record<OfflineQueueApp, OfflineQueueAppState> =>
	OFFLINE_QUEUE_APPS.reduce(
		(acc, app) => {
			acc[app] = emptyAppState();
			return acc;
		},
		{} as Record<OfflineQueueApp, OfflineQueueAppState>
	);

const initialState: OfflineQueueState = {
	byApp: buildInitialByApp(),
	isQueueReadFailed: false,
	isQueueUnavailable: false,
	isReplaying: false,
	isSnapshotLoaded: false,
	recentlyAdmitted: []
};

export const normalizeQueueEntry = (
	descriptor: QueueEntryDescriptor,
	fallbackStatus: QueueEntryStatus
): OfflineQueueEntry => ({
	app: descriptor.app ?? OFFLINE_QUEUE_APP.UNKNOWN,
	attempts: descriptor.attempts ?? 0,
	clientId: descriptor.clientId,
	detail: descriptor.detail ?? null,
	enqueuedAt: descriptor.enqueuedAt ?? Date.now(),
	labelKey: descriptor.labelKey ?? null,
	lastAttemptAt: descriptor.lastAttemptAt ?? null,
	mutationName: descriptor.mutationName,
	reason: null,
	reasonKind: null,
	reconciliationId: descriptor.reconciliationId ?? null,
	...(descriptor.refusal ? { refusal: descriptor.refusal } : {}),
	retryable: true,
	status: descriptor.status ?? fallbackStatus
});

const bucketFor = (state: OfflineQueueState, app: OfflineQueueApp): OfflineQueueAppState => {
	const bucket = state.byApp[app];
	if (bucket) return bucket;
	state.byApp[app] = emptyAppState();
	return state.byApp[app];
};

const dropByClientId = (entries: OfflineQueueEntry[], clientId: string): OfflineQueueEntry[] =>
	entries.filter((entry) => entry.clientId !== clientId);

const forEachBucket = (state: OfflineQueueState, apply: (bucket: OfflineQueueAppState) => void): void => {
	for (const app of Object.keys(state.byApp) as OfflineQueueApp[]) apply(state.byApp[app]);
};

export const offlineQueueSlice = createSlice({
	initialState,
	name: "offlineQueue",
	reducers: {
		dismissAllFailed: (state, action: PayloadAction<OfflineQueueApp | undefined>) => {
			if (action.payload === undefined) {
				forEachBucket(state, (bucket) => {
					bucket.failed = [];
				});
				return;
			}
			bucketFor(state, action.payload).failed = [];
		},
		dismissFailedEntry: (state, action: PayloadAction<string>) => {
			forEachBucket(state, (bucket) => {
				bucket.failed = dropByClientId(bucket.failed, action.payload);
			});
		},
		discardQueueEntry: (state, action: PayloadAction<string>) => {
			forEachBucket(state, (bucket) => {
				bucket.failed = dropByClientId(bucket.failed, action.payload);
				bucket.queued = dropByClientId(bucket.queued, action.payload);
			});
		},
		enqueueMutation: (state, action: PayloadAction<QueueEntryDescriptor>) => {
			const entry = normalizeQueueEntry(action.payload, QUEUE_ENTRY_STATUS.PENDING);
			const bucket = bucketFor(state, entry.app);
			const existing = bucket.queued.findIndex((e) => e.clientId === entry.clientId);
			if (existing >= 0) bucket.queued[existing] = entry;
			else bucket.queued.push(entry);
			if (entry.reconciliationId !== null)
				state.recentlyAdmitted.push({
					app: entry.app,
					mutationName: entry.mutationName,
					reconciliationId: entry.reconciliationId
				});
			if (state.recentlyAdmitted.length > RECENTLY_ADMITTED_LIMIT) state.recentlyAdmitted.shift();
		},
		markQueueReadFailed: (state) => {
			// The worker read its own queue and failed — the state must stay unknown, not flip to unavailable.
			state.isQueueReadFailed = true;
		},
		markQueueReplayFailed: (state, action: PayloadAction<FailedQueueEntryPayload>) => {
			const entry = {
				...normalizeQueueEntry(action.payload, QUEUE_ENTRY_STATUS.FAILED),
				reason: action.payload.reason,
				reasonKind: action.payload.reasonKind ?? null,
				retryable: action.payload.retryable ?? true,
				status: action.payload.status ?? QUEUE_ENTRY_STATUS.FAILED
			};
			const bucket = bucketFor(state, entry.app);
			if (entry.status === QUEUE_ENTRY_STATUS.RETRYING) {
				const queuedIndex = bucket.queued.findIndex((e) => e.clientId === entry.clientId);
				if (queuedIndex >= 0) bucket.queued[queuedIndex] = entry;
				else bucket.queued.push(entry);
				bucket.failed = dropByClientId(bucket.failed, entry.clientId);
				return;
			}
			bucket.queued = dropByClientId(bucket.queued, entry.clientId);
			const failedIndex = bucket.failed.findIndex((e) => e.clientId === entry.clientId);
			if (failedIndex >= 0) bucket.failed[failedIndex] = entry;
			else bucket.failed.push(entry);
		},
		markQueueReplayStarted: (state) => {
			state.isReplaying = true;
		},
		markQueueReplaySuccess: (state, action: PayloadAction<QueueEntryDescriptor>) => {
			const { clientId } = action.payload;
			forEachBucket(state, (bucket) => {
				bucket.queued = dropByClientId(bucket.queued, clientId);
				bucket.failed = dropByClientId(bucket.failed, clientId);
			});
		},
		markQueueUnavailable: (state) => {
			// A snapshot already proved the queue exists; a late timeout must not un-prove it.
			if (!state.isSnapshotLoaded) state.isQueueUnavailable = true;
		},
		markReplayAcknowledged: (state) => {
			state.isReplaying = false;
		},
		setQueueSnapshot: (state, action: PayloadAction<QueueEntryDescriptor[]>) => {
			const next = buildInitialByApp();
			for (const descriptor of action.payload) {
				const entry = normalizeQueueEntry(descriptor, QUEUE_ENTRY_STATUS.PENDING);
				if (!next[entry.app]) next[entry.app] = emptyAppState();
				if (entry.status === QUEUE_ENTRY_STATUS.FAILED) next[entry.app].failed.push(entry);
				else next[entry.app].queued.push(entry);
			}
			state.byApp = next;
			state.isSnapshotLoaded = true;
			// A late-installing SW can answer after the unavailability timeout fired.
			state.isQueueUnavailable = false;
			state.isQueueReadFailed = false;
		}
	}
});

export const {
	discardQueueEntry,
	dismissAllFailed,
	dismissFailedEntry,
	enqueueMutation,
	markQueueReadFailed,
	markQueueReplayFailed,
	markQueueReplayStarted,
	markQueueReplaySuccess,
	markQueueUnavailable,
	markReplayAcknowledged,
	setQueueSnapshot
} = offlineQueueSlice.actions;

export interface OfflineQueueRootState {
	offlineQueue: OfflineQueueState;
}

const EMPTY_APP_STATE: OfflineQueueAppState = { failed: [], queued: [] };

export const selectOfflineQueueForApp =
	(app: OfflineQueueApp) =>
	(state: OfflineQueueRootState): OfflineQueueAppState =>
		state.offlineQueue.byApp[app] ?? EMPTY_APP_STATE;

export const selectIsReplaying = (state: OfflineQueueRootState): boolean => state.offlineQueue.isReplaying;

export const selectIsQueueSnapshotLoaded = (state: OfflineQueueRootState): boolean =>
	state.offlineQueue.isSnapshotLoaded;

// True once the queue's state is settled either way: a snapshot arrived, or there is no service worker to hold one.
// Reconciliation consumers must keep using selectIsQueueSnapshotLoaded — an unavailable queue proves nothing landed.
export const selectIsQueueStateKnown = (state: OfflineQueueRootState): boolean =>
	state.offlineQueue.isSnapshotLoaded || state.offlineQueue.isQueueUnavailable;

export const selectWasRecentlyAdmitted =
	(app: OfflineQueueApp, mutationName: string, reconciliationId: string) =>
	(state: OfflineQueueRootState): boolean =>
		state.offlineQueue.recentlyAdmitted.some(
			(entry) => entry.app === app && entry.mutationName === mutationName && entry.reconciliationId === reconciliationId
		);

export const selectQueuedEntriesForApp =
	(app: OfflineQueueApp) =>
	(state: OfflineQueueRootState): OfflineQueueEntry[] =>
		selectOfflineQueueForApp(app)(state).queued;

export const selectFailedEntriesForApp =
	(app: OfflineQueueApp) =>
	(state: OfflineQueueRootState): OfflineQueueEntry[] =>
		selectOfflineQueueForApp(app)(state).failed;

export const selectQueuedCountForApp =
	(app: OfflineQueueApp) =>
	(state: OfflineQueueRootState): number =>
		selectOfflineQueueForApp(app)(state).queued.length;

export const selectFailedCountForApp =
	(app: OfflineQueueApp) =>
	(state: OfflineQueueRootState): number =>
		selectOfflineQueueForApp(app)(state).failed.length;

const countBlockingForApp =
	(app: OfflineQueueApp) =>
	(state: OfflineQueueRootState): number => {
		const bucket = selectOfflineQueueForApp(app)(state);
		return bucket.queued.length + bucket.failed.filter((entry) => entry.retryable).length;
	};

// Queued plus retryable-failed (incl. parked-until-reauth) mutations — what would replay under a new tenant's token post-switch.
export const selectTotalBlockingQueueCount = (state: OfflineQueueRootState): number =>
	OFFLINE_QUEUE_APPS.reduce((total, app) => total + countBlockingForApp(app)(state), 0);

export default offlineQueueSlice.reducer;
